import React from 'react';
import { Controller, Control, useWatch, useFormContext } from 'react-hook-form';
import { FormControl, FormLabel, InputLabel, MenuItem, Select } from '@mui/material';

import { ConfidenceScoreOperator } from 'generated/graphql';

import {
  CONFIDENCE_CATEGORIES,
  findCategoryByMax,
  findCategoryByValueAndOperator,
} from 'utils/getConfidenceLevel';

import { FormValues } from './index';

interface AIConfidenceScoreFieldsProps {
  control: Control<FormValues>;
  hasAIConfidenceScore: boolean;
}

export const AIConfidenceScoreFields = ({
  control,
  hasAIConfidenceScore,
}: AIConfidenceScoreFieldsProps) => {
  const { getValues, setValue } = useFormContext();

  const operator = useWatch({
    control,
    name: 'confidenceScoreOperator',
  });

  return (
    <div>
      <FormLabel component="legend" sx={{ mb: 2.5 }}>
        AI Confidence Score (optional)
      </FormLabel>
      <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
        <FormControl style={{ width: '100%' }}>
          <Controller
            name="confidenceScoreOperator"
            control={control}
            render={({ field }) => (
              <>
                <InputLabel htmlFor="confidenceScoreOperator">Operator</InputLabel>
                <Select
                  {...field}
                  labelId="AI-confidence-score-label"
                  label="Operator"
                  id="confidenceScoreOperator"
                  disabled={!hasAIConfidenceScore}
                  onChange={(e) => {
                    const newOperator = e.target.value;
                    const oldOperator = field.value;
                    field.onChange(newOperator);

                    const currentScoreValue = getValues('confidenceScoreValue');
                    if (currentScoreValue === undefined) return;

                    const category = findCategoryByValueAndOperator(currentScoreValue, oldOperator);
                    if (!category) return;

                    const newValue =
                      newOperator === ConfidenceScoreOperator.MoreOrEqual
                        ? category.min
                        : category.max;

                    setValue('confidenceScoreValue', newValue);
                  }}
                >
                  <MenuItem value={ConfidenceScoreOperator.MoreOrEqual}>
                    Greater than or equal to
                  </MenuItem>
                  <MenuItem value={ConfidenceScoreOperator.LessOrEqual}>
                    Less than or equal to
                  </MenuItem>
                </Select>
              </>
            )}
          />
        </FormControl>

        <FormControl style={{ width: '100%' }}>
          <Controller
            name="confidenceScoreValue"
            control={control}
            render={({ field }) => {
              let numericValue: number | undefined = undefined;

              if (typeof field.value === 'number') {
                numericValue = field.value;
              } else if (typeof field.value === 'string' && field.value !== '') {
                numericValue = parseFloat(field.value);
              }

              const currentCategory = findCategoryByValueAndOperator(numericValue, operator);

              const selectValue = currentCategory ? currentCategory.max : '';

              return (
                <>
                  <InputLabel id="confidence-score-value-label" htmlFor="confidenceScoreValue">
                    Score
                  </InputLabel>
                  <Select
                    labelId="confidence-score-value-label"
                    id="confidenceScoreValue"
                    label="Score"
                    disabled={!hasAIConfidenceScore}
                    value={selectValue}
                    onChange={(e) => {
                      const newMax = e.target.value as number;
                      const category = findCategoryByMax(newMax);
                      if (!category) return;
                      const newValue =
                        operator === ConfidenceScoreOperator.MoreOrEqual
                          ? category.min
                          : category.max;
                      field.onChange(newValue);
                    }}
                  >
                    {CONFIDENCE_CATEGORIES.map((category) => (
                      <MenuItem key={category.max} value={category.max}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              );
            }}
          />
        </FormControl>
      </div>
    </div>
  );
};
